"use client";
import React, { useInsertionEffect } from "react";
import Button from "antd/es/button";
import Checkbox from "antd/es/checkbox";
import Divider from "antd/es/divider";
import Drawer from "antd/es/drawer";
import Flex from "antd/es/flex";
import List from "antd/es/list";
import Paragraph from "antd/es/typography/Paragraph";
import Text from "antd/es/typography/Text";
import type { SelectedService } from "./types";
import {
  TEMPLATE_LINE_HEIGHT_BODY,
  TEMPLATE_TYPE_SCALE,
} from "../../../TemplateSectionStyle";

// Match MultipleServicesModal's small text (price + description).
const smallStyle: React.CSSProperties = {
  fontSize: TEMPLATE_TYPE_SCALE.small,
  lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
  opacity: 0.7,
};

const EXTRA_OPTIONS_OVERLAY_CLASS = "openings-service-options-overlay";
const EXTRA_OPTIONS_RESPONSIVE_STYLE_ID =
  "openings-service-options-responsive-styles";
const EXTRA_OPTIONS_RESPONSIVE_STYLES = `
.${EXTRA_OPTIONS_OVERLAY_CLASS} > .ant-drawer-content-wrapper {
  height: auto !important;
  max-height: calc(100dvh - 32px);
}

.${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-section {
  color: var(--openings-color-text, #111);
  background: var(--openings-color-surface, #fff);
}

.${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-header,
.${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-footer {
  border-color: var(--openings-color-border, #e5e5e5);
}

.${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-footer .ant-btn {
  min-height: 40px;
}

.${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-close {
  width: 40px;
  min-width: 40px;
  height: 40px;
}

@media (max-width: 767px) {
  .${EXTRA_OPTIONS_OVERLAY_CLASS} > .ant-drawer-content-wrapper {
    max-height: min(86dvh, 640px);
  }

  .${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-section {
    overflow: hidden;
    border-radius:
      var(--openings-booking-radius, 10px)
      var(--openings-booking-radius, 10px)
      0
      0;
  }

  .${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-body {
    overscroll-behavior: contain;
  }

  .${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-footer {
    padding-bottom: max(16px, env(safe-area-inset-bottom));
  }
}

@media (min-width: 768px) {
  .${EXTRA_OPTIONS_OVERLAY_CLASS}.ant-drawer-bottom
    > .ant-drawer-content-wrapper {
    inset-inline: auto !important;
    top: 50% !important;
    right: auto !important;
    bottom: auto !important;
    left: 50% !important;
    width: min(520px, calc(100vw - 48px)) !important;
    max-height: calc(100dvh - 48px);
    transform: translate(-50%, -50%) !important;
  }

  .${EXTRA_OPTIONS_OVERLAY_CLASS} .ant-drawer-section {
    overflow: hidden;
    border-radius: var(--openings-booking-radius, 10px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  }
}
`;

function useExtraOptionsResponsiveStyles(): void {
  useInsertionEffect(() => {
    if (document.getElementById(EXTRA_OPTIONS_RESPONSIVE_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = EXTRA_OPTIONS_RESPONSIVE_STYLE_ID;
    style.textContent = EXTRA_OPTIONS_RESPONSIVE_STYLES;
    document.head.appendChild(style);
  }, []);
}

interface ExtraOptionsProps {
  service: SelectedService;
  showPrice?: boolean;
  onSubmit: (op: SelectedService["option"]) => void;
  onClose: () => void;
}

const ExtraOptionsModal = ({
  service,
  showPrice,
  onSubmit,
  onClose,
}: ExtraOptionsProps) => {
  useExtraOptionsResponsiveStyles();
  const themeAnchorRef = React.useRef<HTMLSpanElement>(null);
  const getOverlayContainer = React.useCallback(
    () =>
      themeAnchorRef.current?.closest<HTMLElement>(
        ".openings-booking-theme-root",
      ) ?? document.body,
    [],
  );

  const DEFAULT_OPTION = {
    id: "",
    title: "Regular",
    price: service.price,
    duration: service.duration,
    description: "",
  };
  const [option, setSelectedOption] = React.useState<SelectedService["option"]>(
    service.option ? service.option : DEFAULT_OPTION,
  );

  const handleSubmit = () => {
    onSubmit(option?.id === "" ? undefined : option);
    onClose();
  };

  const enhancedOptions = service.options
    ? [DEFAULT_OPTION, ...service.options]
    : [DEFAULT_OPTION];

  return (
    <>
      <span
        ref={themeAnchorRef}
        className="openings-service-options-anchor"
        hidden
      />
      <Drawer
        open
        placement="bottom"
        rootClassName={EXTRA_OPTIONS_OVERLAY_CLASS}
        getContainer={getOverlayContainer}
        onClose={onClose}
        zIndex={10100}
        title="Select an option"
        closable={{ placement: "end" }}
        styles={{ wrapper: { height: "auto" } }}
        footer={
          <Flex justify="end" gap="small" wrap>
            <Button type="primary" onClick={handleSubmit}>
              Find Openings
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Flex>
        }
      >
        <Paragraph>
          {`${service.title} options are listed below. Select one if it fits, or skip to the regular menu.`}
        </Paragraph>
        <List
          itemLayout="horizontal"
          dataSource={enhancedOptions}
          renderItem={(item) => (
            <List.Item>
              <Checkbox
                checked={option?.id === item.id}
                onClick={() => {
                  setSelectedOption(item);
                }}
              >
                <Text>
                  {item.title}
                  {showPrice &&
                    typeof item.price === "number" &&
                    item.price > 0 && (
                      <>
                        <Divider orientation="vertical" />
                        <span style={smallStyle}>
                          ${item.price}
                          {item.id === "" && service.up ? "+" : ""}
                        </span>
                      </>
                    )}
                </Text>
                {item.description && (
                  <>
                    <br />
                    <span style={smallStyle}>{item.description}</span>
                  </>
                )}
              </Checkbox>
            </List.Item>
          )}
        />
      </Drawer>
    </>
  );
};

export default ExtraOptionsModal;
